import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import TrialCountdown from '../components/TrialCountdown';
import { 
  User, 
  Camera, 
  Edit3, 
  Save, 
  X, 
  Lock, 
  Eye, 
  EyeOff,
  Trash2,
  AlertTriangle,
  CheckCircle,
  Star,
  Crown,
  CreditCard,
  Settings,
  HelpCircle,
  Shield,
  Brain,
  Smartphone,
  Download,
  Wifi,
  WifiOff,
  Globe,
  TrendingUp,
  Calendar,
  Clock,
  Users,
  MessageCircle,
  BarChart3,
  Volume2,
  Headphones,
  Zap,
  Award,
  Target,
  Heart,
  Sparkles,
  Trophy,
  TrendingDown,
  Minus,
  ArrowUp,
  ArrowDown,
  ArrowRight,
  Lightbulb,
  RefreshCw,
  Phone,
  Mail,
  Video,
  Mic,
  MicOff,
  Check,
  AlertCircle,
  Info,
  ChevronDown,
  ChevronRight
} from 'lucide-react';

const Profile = () => {
  const { user, updateProfile, logout } = useAuth();
  const { subscription, createSubscription, cancelSubscription, updateSubscription } = useSubscription();
  const navigate = useNavigate();
  
  // Check for success message from Stripe redirect
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const sessionId = urlParams.get('session_id');
    
    if (success === 'true' && sessionId) {
      setSuccess('Payment successful! Your subscription has been activated.');
      // Clear the URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);
  
  const [isEditingName, setIsEditingName] = useState(false);
  const [newName, setNewName] = useState(user?.name || '');
  const [profileImage, setProfileImage] = useState(user?.profileImage || null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [openFAQ, setOpenFAQ] = useState(null);
  
  const fileInputRef = useRef(null);

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileImage(e.target.result);
        // Auto-save the image when uploaded
        handleSaveProfileImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveProfileImage = async (imageData) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      console.log('Saving profile image...');
      const updatedUserData = {
        profileImage: imageData
      };

      console.log('Profile data to save:', { profileImage: imageData ? 'Image data present' : 'No image data' });

      const result = await updateProfile(updatedUserData);
      
      console.log('Profile save result:', result);
      
      if (result.success) {
        setSuccess('Profile image updated successfully!');
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.error || 'Failed to save profile image');
      }
    } catch (error) {
      console.error('Error saving profile image:', error);
      setError('Failed to update profile image. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    // Validate name
    if (!newName || newName.trim().length === 0) {
      setError('Name cannot be empty');
      setLoading(false);
      return;
    }

    try {
      console.log('Saving profile...');
      const updatedUserData = {
        name: newName.trim(),
        profileImage: profileImage
      };

      console.log('Profile data to save:', { 
        name: newName.trim(), 
        profileImage: profileImage ? 'Image data present' : 'No image data' 
      });

      const result = await updateProfile(updatedUserData);
      
      console.log('Profile save result:', result);
      
      if (result.success) {
        setIsEditingName(false);
        setSuccess('Profile updated successfully!');
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.error || 'Failed to save profile');
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      setError('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setNewName(user?.name || '');
    setIsEditingName(false);
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
              const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://sabiwabeeai-production.up.railway.app'}/api/user/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          currentPassword,
          newPassword
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Password changed successfully!');
        setShowPasswordModal(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error || 'Failed to change password');
      }
    } catch (error) {
      setError('Failed to change password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone and all your data will be permanently lost.')) {
      if (window.confirm('This is your final warning. All your data, including chat history, assessments, and progress will be permanently deleted. Are you absolutely sure?')) {
        setLoading(true);
        setError('');
        
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://sabiwabeeai-production.up.railway.app'}/api/user/account`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });

          if (response.ok) {
            logout();
            navigate('/login');
          } else {
            const data = await response.json();
            setError(data.error || 'Failed to delete account');
          }
        } catch (error) {
          setError('Failed to delete account. Please try again.');
        } finally {
          setLoading(false);
        }
      }
    }
  };

  const handleSubscriptionAction = async (action, planType = null) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      let result;
      
      switch (action) {
        case 'subscribe':
          result = await createSubscription(planType);
          if (result.success) {
            window.location.href = result.checkoutUrl;
            return;
          }
          break;
        case 'cancel':
          if (window.confirm('Are you sure you want to cancel your subscription? You will lose access to premium features.')) {
            result = await cancelSubscription();
            if (result.success) {
              setSuccess('Subscription cancelled successfully!');
              setShowSubscriptionModal(false);
            }
          } else {
            setLoading(false);
            return;
          }
          break;
        case 'upgrade':
          result = await updateSubscription('advanced');
          if (result.success) {
            setSuccess('Subscription upgraded successfully!');
            setShowSubscriptionModal(false);
          }
          break;
        default:
          break;
      }

      if (!result?.success) {
        setError(result?.error || 'Operation failed. Please try again.');
      }
    } catch (error) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const plans = [
    {
      type: 'base',
      name: 'Base Plan',
      price: '$9.99',
      period: 'month',
      description: 'Perfect for getting started with AI therapy',
      features: [
        'Access to 1 specialized AI therapist',
        '24/7 unlimited chat sessions',
        'Crisis detection & emergency alerts',
        'Evidence-based therapeutic approaches',
        'Basic mood tracking',
        'Session history & analytics',
        'Progress visualization',
        'Goal setting & monitoring',
        'Mental health assessments',
        'Personal journaling',
        'Guided meditation sessions',
        'Coping strategy library',
        'Email support',
        'HIPAA-compliant security',
        'Data encryption',
        'Privacy controls'
      ],
      popular: false
    },
    {
      type: 'advanced',
      name: 'Advanced Plan',
      price: '$19.99',
      period: 'month',
      description: 'Comprehensive therapy with cross-referencing & advanced features',
      features: [
        'Access to ALL 10 specialized AI therapists',
        'Cross-referencing across all therapists',
        'Advanced crisis detection & response',
        'Personalized therapy recommendations',
        'Therapist handover & collaboration',
        'Voice-to-text chat support',
        'Text-to-speech responses',
        'Audio message recording',
        'Voice session capabilities',
        'AI-powered mood analysis',
        'Advanced progress tracking',
        'Behavioral pattern recognition',
        'Personalized insights & recommendations',
        'Long-term progress reports',
        'Priority customer support',
        'Phone & video support',
        'Custom therapy goals',
        'Advanced journaling with AI insights',
        'Meditation playlist customization',
        'Peer support community access',
        'Progressive Web App (PWA)',
        'Install to home screen',
        'Offline content access',
        'Mobile-optimized experience',
        'Enhanced data protection',
        'Advanced privacy controls',
        'Data export capabilities',
        'Granular consent management'
      ],
      popular: true
    }
  ];

  const getCurrentPlan = () => {
    if (!subscription) return null;
    
    // If user is on trial, return trial plan info
    if (subscription.status === 'trial') {
      return {
        type: 'trial',
        name: 'Free Trial',
        description: '7-day free trial with access to all features',
        price: 'Free',
        period: '7 days',
        features: [
          'Access to all therapists',
          'Unlimited chat sessions',
          'Progress tracking',
          'All premium features'
        ]
      };
    }
    
    // Find the base plan
    const plan = plans.find(plan => plan.type === subscription.planType);
    
    // If subscription is being canceled, modify the description
    if (subscription.cancelAtPeriodEnd) {
      const daysRemaining = subscription.cancellationInfo?.daysRemaining || 0;
      return {
        ...plan,
        name: `${plan.name} (Canceling)`,
        description: `Your subscription will end on ${new Date(subscription.currentPeriodEnd).toLocaleDateString()} (${daysRemaining} days remaining)`,
        isCanceling: true
      };
    }
    
    return plan;
  };

  const currentPlan = getCurrentPlan();

  const faqData = [
    {
      id: 'account',
      title: 'Account & Profile',
      icon: User,
      questions: [
        {
          question: 'How do I change my profile picture?',
          answer: 'Click on the camera icon next to your profile picture in the Profile section. You can upload any image file (JPG, PNG, etc.) and it will be automatically saved and displayed.'
        },
        {
          question: 'Can I change my display name?',
          answer: 'Yes! Click the edit icon next to your name in the Profile section. Enter your new name and click "Save" to update it immediately.'
        },
        {
          question: 'How do I change my password?',
          answer: 'Click "Change Password" in the Profile section. You\'ll need to enter your current password, then your new password twice for confirmation. Passwords must be at least 8 characters long.'
        },
        {
          question: 'Is my personal information secure?',
          answer: 'Absolutely! We use enterprise-grade encryption and HIPAA-compliant security measures. Your data is encrypted both in transit and at rest, and we never share your personal information with third parties.'
        }
      ]
    },
    {
      id: 'subscription',
      title: 'Subscription & Billing',
      icon: CreditCard,
      questions: [
        {
          question: 'What\'s the difference between Base and Advanced plans?',
          answer: 'Base Plan ($9.99/month) includes 1 AI therapist, basic features, and email support. Advanced Plan ($19.99/month) includes ALL 10 therapists, voice features, PWA installation, peer support, advanced analytics, and priority support.'
        },
        {
          question: 'Can I upgrade or downgrade my plan?',
          answer: 'Yes! You can upgrade to Advanced anytime for immediate access to all features. To downgrade, contact support and it will take effect at your next billing cycle.'
        },
        {
          question: 'How do I cancel my subscription?',
          answer: 'Click "Manage Subscription" in your Profile, then "Cancel Subscription". You\'ll continue to have access until the end of your current billing period. No hidden fees or cancellation charges.'
        },
        {
          question: 'What payment methods do you accept?',
          answer: 'We accept all major credit cards, debit cards, and digital wallets through secure Stripe processing. All payments are encrypted and protected by bank-level security.'
        },
        {
          question: 'Is there a free trial?',
          answer: 'New users get a 30-day free trial with the Base Plan to experience our platform. No credit card required to start your trial.'
        }
      ]
    },
    {
      id: 'therapy',
      title: 'AI Therapy & Features',
      icon: Brain,
      questions: [
        {
          question: 'How do the AI therapists work?',
          answer: 'Our AI therapists use advanced language models trained on evidence-based therapeutic approaches. They provide personalized support, crisis detection, and can cross-reference your history across sessions for continuity of care.'
        },
        {
          question: 'What if I need crisis support?',
          answer: 'Our AI includes crisis detection and will immediately provide emergency resources. For immediate help, call 988 (US) or your local crisis hotline. Sabiwabee is not a substitute for emergency care.'
        },
        {
          question: 'Can I use voice features?',
          answer: 'Voice features (voice-to-text, text-to-speech, audio recording) are available with the Advanced Plan. Base Plan users can upgrade anytime to access these features.'
        },
        {
          question: 'How does cross-referencing work?',
          answer: 'Advanced Plan users can access all 10 therapists, and the AI can reference your chat history across all therapists to provide more personalized and continuous care.'
        },
        {
          question: 'What therapeutic approaches do you use?',
          answer: 'Our AI therapists are trained on evidence-based approaches including CBT, DBT, mindfulness, and other clinically validated therapeutic techniques.'
        }
      ]
    },
    {
      id: 'features',
      title: 'App Features & Tools',
      icon: Settings,
      questions: [
        {
          question: 'What is the PWA feature?',
          answer: 'Progressive Web App (PWA) allows you to install Sabiwabee to your home screen like a native app. Available with Advanced Plan, it provides offline access and a more app-like experience.'
        },
        {
          question: 'How does mood tracking work?',
          answer: 'Track your daily mood, anxiety, and stress levels. Advanced Plan users get AI-powered analysis and insights to identify patterns and trends over time.'
        },
        {
          question: 'What are the assessments?',
          answer: 'Mental health assessments help evaluate your current state and provide personalized recommendations. Results are private and used to improve your therapy experience.'
        },
        {
          question: 'How does the journal work?',
          answer: 'Write private journal entries to track your thoughts and progress. Advanced Plan users get AI-generated insights and analysis of their journal entries.'
        },
        {
          question: 'What is peer support?',
          answer: 'Advanced Plan users can access our peer support community to connect with others on similar journeys. All interactions are moderated and anonymous.'
        },
        {
          question: 'How do meditation sessions work?',
          answer: 'Access guided meditation sessions for stress relief and mindfulness. Advanced Plan users can customize playlists and access premium content.'
        }
      ]
    },
    {
      id: 'privacy',
      title: 'Privacy & Security',
      icon: Shield,
      questions: [
        {
          question: 'Is my data private?',
          answer: 'Yes! All conversations are encrypted and protected by HIPAA-compliant security measures. We never share your data with third parties without your explicit consent.'
        },
        {
          question: 'Can I export my data?',
          answer: 'Advanced Plan users can export their data including chat history, journal entries, and progress reports. Contact support to request your data export.'
        },
        {
          question: 'How do you handle consent?',
          answer: 'We provide granular consent management. You can control what data is collected and how it\'s used. You can withdraw consent at any time.'
        },
        {
          question: 'What happens if I delete my account?',
          answer: 'Account deletion permanently removes all your data from our systems. This action cannot be undone, so make sure to export any data you want to keep first.'
        },
        {
          question: 'Are my conversations recorded?',
          answer: 'Text conversations are stored securely for continuity of care. Voice recordings (Advanced Plan) are processed in real-time and not permanently stored unless you explicitly consent.'
        }
      ]
    },
    {
      id: 'technical',
      title: 'Technical Support',
      icon: HelpCircle,
      questions: [
        {
          question: 'What devices are supported?',
          answer: 'Sabiwabee works on all modern browsers (Chrome, Safari, Firefox, Edge) on desktop, tablet, and mobile devices. Advanced Plan users can install it as a PWA on mobile.'
        },
        {
          question: 'Do I need an internet connection?',
          answer: 'Most features require an internet connection. Advanced Plan users can access some content offline when using the PWA installation.'
        },
        {
          question: 'How do I get help?',
          answer: 'Base Plan users can contact us via email. Advanced Plan users get priority support including phone and video support options.'
        },
        {
          question: 'What if the app isn\'t working?',
          answer: 'Try refreshing the page or clearing your browser cache. If issues persist, contact support with details about your device and browser.'
        },
        {
          question: 'Can I use multiple devices?',
          answer: 'Yes! Your account syncs across all devices. Simply log in on any device to access your therapy sessions, journal, and progress data.'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-500/10 to-secondary-500/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full mb-6 shadow-lg">
              <User className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Profile Settings
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Manage your account, subscription, and personal information
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {/* Trial Countdown */}
        <TrialCountdown />

        {/* Success/Error Messages */}
        {success && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-4 mb-8 shadow-sm">
            <div className="flex items-center">
              <div className="p-2 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg mr-3">
                <Check className="w-5 h-5 text-white" />
              </div>
              <span className="text-green-800 font-medium">{success}</span>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-2xl p-4 mb-8 shadow-sm">
            <div className="flex items-center">
              <div className="p-2 bg-gradient-to-r from-red-500 to-pink-500 rounded-lg mr-3">
                <AlertCircle className="w-5 h-5 text-white" />
              </div>
              <span className="text-red-800 font-medium">{error}</span>
            </div>
          </div>
        )}

        {/* Cancellation Banner */}
        {subscription?.cancelAtPeriodEnd && (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6 mb-8 shadow-sm">
            <div className="flex items-start space-x-4">
              <div className="p-3 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl flex-shrink-0">
                <AlertCircle className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-amber-800 mb-2">Subscription Canceling</h3>
                <p className="text-amber-700 mb-3">
                  Your subscription has been canceled and will end on{' '}
                  <span className="font-semibold">
                    {subscription.currentPeriodEnd ? new Date(subscription.currentPeriodEnd).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    }) : 'the end of your billing period'}
                  </span>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Profile Section */}
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-xl border border-gray-100 p-8 mb-8">
        <div className="flex items-center space-x-3 mb-8">
          <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center shadow-lg">
            <User className="w-6 h-6 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Profile Information</h2>
        </div>
        
        <div className="flex flex-col md:flex-row items-start space-y-6 md:space-y-0 md:space-x-8">
          {/* Profile Photo */}
          <div className="flex-shrink-0">
            <div className="relative">
              <div className="w-32 h-32 rounded-3xl overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 border-4 border-white shadow-xl">
                {profileImage ? (
                  <img 
                    src={profileImage} 
                    alt="Profile" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <User className="w-16 h-16 text-gray-400" />
                  </div>
                )}
              </div>
              
              <button
                onClick={() => fileInputRef.current?.click()}
                className="absolute -bottom-2 -right-2 w-10 h-10 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl flex items-center justify-center text-white hover:from-primary-600 hover:to-secondary-600 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                title="Change photo"
              >
                <Camera className="w-5 h-5" />
              </button>
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
            </div>
          </div>

          {/* Profile Details */}
          <div className="flex-1 space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Email Address
              </label>
              <div className="relative">
                <input
                  type="email"
                  value={user?.email || ''}
                  disabled
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl bg-gray-50 text-gray-500 font-medium"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <Mail className="w-5 h-5 text-gray-400" />
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-2 flex items-center space-x-1">
                <Lock className="w-4 h-4" />
                <span>Email cannot be changed for security reasons</span>
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Display Name
              </label>
              {isEditingName ? (
                <div className="space-y-4">
                  <div className="flex space-x-3">
                    <input
                      type="text"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                      placeholder="Enter your name"
                    />
                    <button
                      onClick={handleSaveProfile}
                      disabled={loading}
                      className="px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl hover:from-primary-600 hover:to-secondary-600 disabled:opacity-50 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center space-x-2"
                    >
                      {loading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          <span>Save</span>
                        </>
                      )}
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-all duration-200 transform hover:scale-105"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-sm text-gray-500 flex items-center space-x-1">
                    <Info className="w-4 h-4" />
                    <span>Enter your first name, or first and last name separated by a space</span>
                  </p>
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  <input
                    type="text"
                    value={user?.name || ''}
                    disabled
                    className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl bg-gray-50 text-gray-500 font-medium"
                  />
                  <button
                    onClick={() => setIsEditingName(true)}
                    className="px-4 py-3 text-primary-600 hover:bg-primary-50 rounded-xl transition-all duration-200 transform hover:scale-105"
                  >
                    <Edit3 className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

        {/* Subscription Section */}
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-xl border border-gray-100 p-8 mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center shadow-lg">
                <CreditCard className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Subscription</h2>
            </div>
            <button
              onClick={() => setShowSubscriptionModal(true)}
              className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl hover:from-green-600 hover:to-emerald-600 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center justify-center space-x-2"
            >
              <Settings className="w-4 h-4" />
              <span>Manage Subscription</span>
            </button>
          </div>

          {currentPlan ? (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-8 border border-green-100">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between space-y-4 md:space-y-0">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-3">
                    <h3 className="text-2xl font-bold text-gray-900">{currentPlan.name}</h3>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-800">
                      <Check className="w-4 h-4 mr-1" />
                      Active
                    </span>
                  </div>
                  <p className="text-gray-600 text-lg mb-3">{currentPlan.description}</p>
                  <p className="text-3xl font-bold text-green-600">
                    {currentPlan.price}
                    <span className="text-lg font-normal text-gray-500">/{currentPlan.period}</span>
                  </p>
                  
                  {/* Cancellation Information */}
                  {subscription?.cancelAtPeriodEnd && (
                    <div className="mt-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200">
                      <div className="flex items-start space-x-3">
                        <div className="w-8 h-8 bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
                          <AlertCircle className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-amber-800 mb-1">Subscription Canceling</h4>
                          <p className="text-amber-700 text-sm mb-2">
                            Your subscription will end on{' '}
                            <span className="font-semibold">
                              {subscription.currentPeriodEnd ? new Date(subscription.currentPeriodEnd).toLocaleDateString('en-US', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              }) : 'the end of your billing period'}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center shadow-lg">
                    <Star className="w-8 h-8 text-white" />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-gradient-to-r from-gray-400 to-gray-500 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <CreditCard className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">No Active Subscription</h3>
              <p className="text-gray-600 text-lg mb-6 max-w-md mx-auto">Upgrade to access premium features and unlock the full potential of your therapy journey</p>
              <button
                onClick={() => setShowSubscriptionModal(true)}
                className="px-8 py-4 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl hover:from-primary-600 hover:to-secondary-600 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center space-x-2 mx-auto"
              >
                <ArrowRight className="w-5 h-5" />
                <span>View Plans</span>
              </button>
            </div>
          )}
        </div>

        {/* Security Section */}
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-xl border border-gray-100 p-8 mb-8">
          <div className="flex items-center space-x-3 mb-8">
            <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-red-600 rounded-2xl flex items-center justify-center shadow-lg">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Security & Privacy</h2>
          </div>
          
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 border border-blue-100 hover:shadow-md transition-all duration-300">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-4 sm:space-y-0">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
                    <Lock className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-gray-900">Change Password</h3>
                    <p className="text-gray-600">Update your account password for enhanced security</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowPasswordModal(true)}
                  className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center justify-center space-x-2"
                >
                  <Lock className="w-4 h-4" />
                  <span>Change Password</span>
                </button>
              </div>
            </div>

            <div className="bg-gradient-to-r from-red-50 to-pink-50 rounded-2xl p-6 border border-red-100 hover:shadow-md transition-all duration-300">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-4 sm:space-y-0">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
                    <Trash2 className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-red-900">Delete Account</h3>
                    <p className="text-red-700">Permanently delete your account and all associated data</p>
                  </div>
                </div>
                <button
                  onClick={handleDeleteAccount}
                  disabled={loading}
                  className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl hover:from-red-600 hover:to-red-700 disabled:opacity-50 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center justify-center space-x-2"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      <span>Delete Account</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Change Password</h2>
              <button
                onClick={() => setShowPasswordModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter current password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Confirm new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={handleChangePassword}
                  disabled={loading || !currentPassword || !newPassword || !confirmPassword}
                  className="flex-1 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 transition-colors duration-200"
                >
                  {loading ? 'Changing...' : 'Change Password'}
                </button>
                <button
                  onClick={() => setShowPasswordModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors duration-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Modal */}
      {showSubscriptionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">Subscription Plans</h2>
                <button
                  onClick={() => setShowSubscriptionModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                {plans.map((plan) => (
                  <div
                    key={plan.type}
                    className={`relative border rounded-lg p-6 ${
                      plan.popular ? 'border-primary-500 bg-primary-50' : 'border-gray-200'
                    }`}
                  >
                    {plan.popular && (
                      <span className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-primary-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                        Most Popular
                      </span>
                    )}

                    <div className="text-center mb-4">
                      <h3 className="text-xl font-semibold text-gray-900">{plan.name}</h3>
                      <p className="text-gray-600 text-sm">{plan.description}</p>
                      <p className="text-3xl font-bold text-primary-600 mt-2">
                        {plan.price}
                        <span className="text-lg font-normal text-gray-500">/{plan.period}</span>
                      </p>
                    </div>

                    <ul className="space-y-3 mb-6">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-start">
                          <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="space-y-2">
                      {currentPlan?.type === plan.type ? (
                        <div className="text-center">
                          <span className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium bg-green-100 text-green-800">
                            Current Plan
                          </span>
                        </div>
                      ) : (
                        <>
                          {currentPlan && currentPlan.type === 'base' && plan.type === 'advanced' && (
                            <button
                              onClick={() => handleSubscriptionAction('upgrade')}
                              disabled={loading}
                              className="w-full px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 transition-colors duration-200"
                            >
                              {loading ? 'Upgrading...' : 'Upgrade Plan'}
                            </button>
                          )}
                          {(!currentPlan || (currentPlan.type === 'advanced' && plan.type === 'base')) && (
                            <button
                              onClick={() => handleSubscriptionAction('subscribe', plan.type)}
                              disabled={loading}
                              className="w-full px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 transition-colors duration-200"
                            >
                              {loading ? 'Processing...' : 'Subscribe'}
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {currentPlan && (
                <div className="border-t border-gray-200 pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Current Subscription</h3>
                      <p className="text-gray-600">Manage your existing subscription</p>
                    </div>
                    {!subscription?.cancelAtPeriodEnd && (
                      <button
                        onClick={() => handleSubscriptionAction('cancel')}
                        disabled={loading}
                        className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 transition-colors duration-200"
                      >
                        {loading ? 'Processing...' : 'Cancel Subscription'}
                      </button>
                    )}
                  </div>

                  {/* Cancellation Information in Modal */}
                  {subscription?.cancelAtPeriodEnd && (
                    <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 mb-4">
                      <div className="flex items-start space-x-3">
                        <div className="w-8 h-8 bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
                          <AlertCircle className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-amber-800 mb-1">Subscription Canceling</h4>
                          <p className="text-amber-700 text-sm mb-2">
                            Your subscription will end on{' '}
                            <span className="font-semibold">
                              {subscription.currentPeriodEnd ? new Date(subscription.currentPeriodEnd).toLocaleDateString('en-US', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              }) : 'the end of your billing period'}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

        {/* FAQ Section */}
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-xl border border-gray-100 p-8">
          <div className="flex items-center space-x-3 mb-8">
            <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg">
              <HelpCircle className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Frequently Asked Questions</h2>
          </div>
          
          <div className="space-y-6">
            {faqData.map((category) => {
              const Icon = category.icon;
              return (
                <div key={category.id} className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden">
                  <button
                    onClick={() => setOpenFAQ(openFAQ === category.id ? null : category.id)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-gradient-to-r hover:from-gray-50 hover:to-blue-50 transition-all duration-200"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center shadow-lg">
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <span className="text-lg font-bold text-gray-900">{category.title}</span>
                    </div>
                    {openFAQ === category.id ? (
                      <ChevronDown className="w-6 h-6 text-gray-500" />
                    ) : (
                      <ChevronRight className="w-6 h-6 text-gray-500" />
                    )}
                  </button>
                  
                  {openFAQ === category.id && (
                    <div className="border-t border-gray-200 bg-gradient-to-r from-gray-50 to-blue-50">
                      <div className="p-6 space-y-6">
                        {category.questions.map((faq, index) => (
                          <div key={index} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300">
                            <h4 className="text-lg font-bold text-gray-900 mb-3 flex items-center space-x-2">
                              <div className="w-2 h-2 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full"></div>
                              <span>{faq.question}</span>
                            </h4>
                            <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;